import React, { useEffect, useState } from 'react';
import { Card, Tag, Spin, message, Empty, Collapse, Modal } from 'antd';
import { CalendarOutlined, ToolOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import api from '../../api/axios';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { formatMoney } from '../../utils/money';

const TYPE_LABELS = { normal: 'Normal', regular: 'Normal', trial: 'Deneme', makeup: 'Telafi', one_time: 'Tek Seferlik' };
const TYPE_COLORS = { normal: 'blue', regular: 'blue', trial: 'gold', makeup: 'orange', one_time: 'geekblue' };

const DAYS_TR = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

const ParentDashboard = () => {
  const [loading, setLoading]     = useState(true);
  const [data, setData]           = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    api.get('/parent/dashboard')
      .then(res => setData(res.data))
      .catch(() => message.error('Veriler yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center items-center h-64"><Spin size="large" /></div>;
  if (!data)   return null;

  const { week_sessions = [], latest_announcements = [], next_makeup, debt_summary } = data;

  const formatSessionDate = (session) => {
    const d = dayjs(session.date);
    const dayName = DAYS_TR[d.day()];
    const time = session.start_time?.slice(0, 5) || '';
    return `${dayName} - ${time}`;
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-0">Genel Bakış</h2>

      {/* Duyurular */}
      {latest_announcements.length > 0 && (
        <Card title="Son Duyurular" size="small">
          <Collapse
            ghost
            size="small"
            items={latest_announcements.map(a => ({
              key: a.id,
              label: (
                <div className="flex justify-between items-center w-full pr-4">
                  <span className="font-semibold text-sm">{a.title}</span>
                  <span className="text-xs text-gray-400 shrink-0 ml-2">
                    {dayjs(a.published_at).format('DD.MM.YYYY')}
                  </span>
                </div>
              ),
              children: (
                <div style={{ padding: '4px 8px' }}>
                  {a.image_url && (
                    <img
                      src={a.image_url}
                      alt=""
                      style={{ maxHeight: 200, borderRadius: 8, marginBottom: 8, display: 'block', maxWidth: '100%', cursor: 'zoom-in' }}
                      onClick={() => setLightboxUrl(a.image_url)}
                    />
                  )}
                  {a.body && (
                    <div
                      className="text-gray-600 text-sm ql-editor prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.body) }}
                    />
                  )}
                </div>
              ),
            }))}
          />
        </Card>
      )}

      {/* Bu hafta planlanmış dersler */}
      <Card title={<span><CalendarOutlined className="mr-2" />Bu hafta planlanmış dersler</span>} size="small">
        {week_sessions.length === 0 ? (
          <Empty description="Bu hafta ders yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="space-y-1">
            {week_sessions.map(s => (
              <div key={s.id} className="flex justify-between items-center py-1.5 border-b last:border-0">
                <div>
                  <span className="text-sm font-medium">{formatSessionDate(s)}</span>
                  {s.student_name && (
                    <span className="text-xs text-gray-400 ml-2">{s.student_name}</span>
                  )}
                </div>
                <Tag color={TYPE_COLORS[s.type] || 'default'} style={{ margin: 0, fontSize: 10 }}>
                  {TYPE_LABELS[s.type] || s.type}
                </Tag>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/*
        HESAP DURUMU — "Yaklaşan Ödeme" tahmininin yerine geçti (15.10.2026).

        Eski kutu ileriye dönük bir TAHMİN gösteriyordu ("4 ders × 700 ₺,
        1–28 Ekim arası dersler için"). Veliler de Eren Bey de anlamadı:
        tahminin neye dayandığı ekrandan doğrulanamıyordu, Ödemeler
        sayfasındaki rakamlarla eşleşmiyordu ve "bunu ödeyecek miyim, ne
        zaman?" sorusunu cevapsız bırakıyordu.

        Yerine GERÇEKLEŞMİŞ durum konuldu: yapılmış derslerin tahakkuku
        eksi yapılmış ödemeler. Bu sayı doğrulanabilir — veli Ödemeler
        sayfasını açıp kalemleri tek tek görebiliyor.

        ÜÇ DURUM var, ikisi değil: `debt_summary.total_debt` backend'de
        `max(0, ...)` ile kırpılıyor, yani PEŞİN ÖDEME yapan velide 0
        görünüyor ve alacağı kayboluyor. Kurum 4 haftada bir peşin tahsil
        ettiği için bu nadir değil, olağan hâl. Bu yüzden fark burada
        `total_charged` ve `total_paid`'den yeniden hesaplanıyor.
      */}
      {debt_summary && (() => {
        const tahakkuk = Number(debt_summary.total_charged) || 0;
        const odenen   = Number(debt_summary.total_paid)    || 0;
        const fark     = tahakkuk - odenen;           // >0 borç, <0 alacak

        const borclu = fark > 0.005;
        const renk   = borclu ? '#fa8c16' : '#52c41a';

        return (
          <Card size="small" style={{ borderColor: renk }}>
            <div className="flex items-center gap-2 mb-1">
              <DollarOutlined style={{ color: renk }} />
              <span className="font-medium text-sm">Hesap Durumu</span>
            </div>
            <div className="text-sm text-gray-700 space-y-0.5">
              <div>
                {borclu ? (
                  <>
                    <span className="text-gray-500">Ödenmemiş Tutar:</span>{' '}
                    <span className="font-bold" style={{ color: renk }}>{formatMoney(fark)}</span>
                  </>
                ) : fark < -0.005 ? (
                  <>
                    <span className="text-gray-500">Bakiyeniz:</span>{' '}
                    <span className="font-bold" style={{ color: renk }}>{formatMoney(-fark)}</span>
                    <span className="text-xs text-gray-500 ml-1">(alacaklı)</span>
                  </>
                ) : (
                  <span className="font-semibold" style={{ color: renk }}>Güncel borcunuz yok</span>
                )}
              </div>
              {/* Rakamın nereden geldiği YAZILI olmalı — eski kutunun asıl
                  sorunu, tutarın doğrulanamamasıydı. */}
              <div className="text-xs text-gray-400">
                Toplam tahakkuk {formatMoney(tahakkuk)} · Ödenen {formatMoney(odenen)}
              </div>
              <div className="text-xs text-gray-400">
                Yapılmış dersler üzerinden hesaplanır. Ayrıntı için Ödemeler sayfasına bakabilirsiniz.
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Yaklaşan telafi */}
      {next_makeup && (
        <Card size="small" style={{ borderColor: '#fa8c16' }}>
          <div className="flex items-center gap-2">
            <ToolOutlined style={{ color: '#fa8c16' }} />
            <span className="font-medium text-sm">Yaklaşan Telafi Dersi</span>
          </div>
          <div className="mt-1 text-sm text-gray-600">
            {next_makeup.classroom} · {DAYS_TR[dayjs(next_makeup.date).day()]} {dayjs(next_makeup.date).format('DD.MM.YYYY')} · {next_makeup.start_time?.slice(0, 5)}
            {next_makeup.end_time ? ` – ${next_makeup.end_time.slice(0, 5)}` : ''}
          </div>
        </Card>
      )}
      <Modal
        open={!!lightboxUrl}
        onCancel={() => setLightboxUrl(null)}
        footer={null}
        width="90vw"
        style={{ maxWidth: 1000, top: 20 }}
        styles={{ body: { padding: 0, textAlign: 'center', background: '#000' } }}
        destroyOnClose
      >
        {lightboxUrl && (
          <img
            src={lightboxUrl}
            alt=""
            style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block', margin: '0 auto' }}
          />
        )}
      </Modal>
    </div>
  );
};

export default ParentDashboard;
